/**
 * Super Admin — Plan Presets editor.
 *
 * Reads /api/platform/plan-presets and lets super_admin edit each preset
 * inline. Every save creates a `platform.plan_preset.update` audit row.
 * Applied presets are snapshotted per-workspace so edits here do NOT
 * silently rewrite live subscriptions (see plan_presets_service.py).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PencilSimple, FloppyDisk } from "@phosphor-icons/react";

const NUMERIC_FIELDS = new Set([
  "student_limit", "user_limit", "branch_limit", "storage_gb_limit",
  "monthly_price", "annual_price",
]);

export default function PlanPresetsTab() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // {key, patch}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/plan-presets");
      setRows(data.presets || {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (key) => setEditing({ key, patch: { ...(rows[key] || {}) } });
  const closeEdit = () => setEditing(null);

  const setField = (name, val) => setEditing(e => ({
    ...e, patch: { ...e.patch, [name]: NUMERIC_FIELDS.has(name) ? Number(val) : val },
  }));

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { ...editing.patch };
      // Strip fields the API doesn't accept for edits.
      delete payload.kind;
      await api.put(`/platform/plan-presets/${editing.key}`, payload);
      toast.success("Preset updated");
      closeEdit();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (rows === null) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const entries = Object.entries(rows);

  return (
    <div className="space-y-4" data-testid="plan-presets-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            Editable Plan Presets
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Editing a preset does NOT rewrite existing workspace subscriptions — a preset&apos;s
            values are snapshotted when applied to a workspace. Existing subscribers keep the
            prices they signed up with.
          </p>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead className="text-right">Monthly (₹)</TableHead>
                <TableHead className="text-right">Annual (₹)</TableHead>
                <TableHead className="text-right">Students</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Storage (GB)</TableHead>
                <TableHead>Visible</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(([key, p]) => (
                <TableRow key={key} data-testid={`plan-preset-row-${key}`}>
                  <TableCell className="font-mono text-xs">{key}</TableCell>
                  <TableCell>{p.tier_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">{p.kind || "base"}</Badge>
                    {p.enquiry_only && (
                      <Badge className="ml-1 text-[10px] bg-primary/10 text-primary border-primary/20">Talk to us</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p.enquiry_only ? "—" : Number(p.monthly_price || 0).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p.enquiry_only ? "—" : Number(p.annual_price || 0).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell className="text-right">{p.student_limit === -1 ? "∞" : p.student_limit}</TableCell>
                  <TableCell className="text-right">{p.user_limit === -1 ? "∞" : p.user_limit}</TableCell>
                  <TableCell className="text-right">{p.storage_gb_limit === -1 ? "∞" : p.storage_gb_limit}</TableCell>
                  <TableCell>
                    <Badge variant={p.visible ? "default" : "outline"} className="text-[10px]">
                      {p.visible ? "yes" : "no"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openEdit(key)} data-testid={`plan-preset-edit-${key}`}>
                      <PencilSimple size={13} className="mr-1" /> Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="sm:max-w-lg bg-card">
          <DialogHeader>
            <DialogTitle>Edit preset — {editing?.patch.tier_name}</DialogTitle>
            <DialogDescription className="text-xs">
              Changes save immediately. Existing workspace subscriptions are NOT rewritten — they
              use their own per-workspace snapshot.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <FieldRow name="tier_name" label="Display name" value={editing.patch.tier_name || ""} onChange={setField} />
              <FieldRow name="billing_cycle" label="Billing cycle" value={editing.patch.billing_cycle || ""} onChange={setField} placeholder="monthly / annual / trial / custom" />
              <FieldRow name="monthly_price" label="Monthly price (₹)" value={editing.patch.monthly_price ?? 0} onChange={setField} type="number" />
              <FieldRow name="annual_price" label="Annual price (₹)" value={editing.patch.annual_price ?? 0} onChange={setField} type="number" />
              <FieldRow name="student_limit" label="Student limit (-1 = ∞)" value={editing.patch.student_limit ?? 0} onChange={setField} type="number" />
              <FieldRow name="user_limit" label="User limit (-1 = ∞)" value={editing.patch.user_limit ?? 0} onChange={setField} type="number" />
              <FieldRow name="branch_limit" label="Branch limit (-1 = ∞)" value={editing.patch.branch_limit ?? 0} onChange={setField} type="number" />
              <FieldRow name="storage_gb_limit" label="Storage GB (-1 = ∞)" value={editing.patch.storage_gb_limit ?? 0} onChange={setField} type="number" />
              <div className="col-span-2 flex items-center gap-6 pt-2 border-t border-border">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={!!editing.patch.visible}
                    onChange={e => setField("visible", e.target.checked)}
                    data-testid="preset-edit-visible"
                  />
                  Visible on Subscription page
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={!!editing.patch.enquiry_only}
                    onChange={e => setField("enquiry_only", e.target.checked)}
                    data-testid="preset-edit-enquiry-only"
                  />
                  Enquiry-only (Talk to us)
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="plan-preset-save">
              <FloppyDisk size={13} className="mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldRow({ name, label, value, onChange, type = "text", placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        data-testid={`preset-edit-${name}`}
      />
    </div>
  );
}
